/**
 * Mux Video Configuration
 * =======================
 * Configuration for Mux video hosting and playback.
 *
 * Mux provides:
 * - Video hosting and transcoding
 * - Adaptive bitrate streaming
 * - Thumbnail generation
 * - Direct browser uploads
 * - Analytics and insights
 *
 * @see https://docs.mux.com/
 */

export const muxConfig = {
  // Mux environment key (safe to expose in frontend)
  ENV_KEY: import.meta.env.VITE_MUX_ENV_KEY || '',

  // Video playback settings
  playback: {
    // Default player theme colors (can be customized)
    accentColor: '#22c55e', // primary-500
    primaryColor: '#ffffff',

    // Player features
    autoPlay: false,
    muted: false,
    loop: false,

    // Streaming quality settings
    preferPlayback: 'mse' as const, // 'mse' | 'native'

    // Thumbnail settings
    thumbnailTime: 0, // seconds
  },

  // Upload settings
  upload: {
    // Maximum file size (3 minutes at high quality ≈ 500MB)
    maxFileSizeMB: 500,

    // Allowed video types
    allowedTypes: ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm'],

    // Max video duration (3 minutes as per spec)
    maxDurationSeconds: 180,
  },

  /**
   * Check if Mux is properly configured
   */
  isConfigured(): boolean {
    return Boolean(this.ENV_KEY)
  },

  /**
   * Generate a playback URL from a playback ID
   */
  getPlaybackUrl(playbackId: string): string {
    return `https://stream.mux.com/${playbackId}.m3u8`
  },

  /**
   * Generate a thumbnail URL from a playback ID
   */
  getThumbnailUrl(
    playbackId: string,
    options: {
      width?: number
      height?: number
      time?: number
      fitMode?: 'preserve' | 'stretch' | 'crop' | 'smartcrop' | 'pad'
    } = {}
  ): string {
    const { width = 640, height = 360, time = 0, fitMode = 'smartcrop' } = options
    return `https://image.mux.com/${playbackId}/thumbnail.jpg?width=${width}&height=${height}&time=${time}&fit_mode=${fitMode}`
  },

  /**
   * Generate an animated GIF preview URL
   */
  getGifUrl(
    playbackId: string,
    options: {
      width?: number
      fps?: number
      start?: number
      end?: number
    } = {}
  ): string {
    const { width = 320, fps = 15, start = 0, end = 5 } = options
    return `https://image.mux.com/${playbackId}/animated.gif?width=${width}&fps=${fps}&start=${start}&end=${end}`
  },

  /**
   * Log configuration status
   */
  logStatus() {
    console.log('📊 Mux Configuration:')
    console.log(`   Configured: ${this.isConfigured() ? '✓' : '✗'}`)
    console.log(`   Environment Key: ${this.ENV_KEY ? '✓ Set' : '✗ Missing'}`)
  },
}
